import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Send } from "lucide-react";
import BriefingAnalytics from "./BriefingAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUSES = ["New", "Reviewing", "More information required", "Suitable for introductory meeting", "NDA required", "Proposal development", "Declined", "Archived"];
const CHECKLIST = ["Recipient identity confirmed", "Organisational authority confirmed", "Cultural authority considered", "Engagement purpose documented", "Information to be disclosed classified", "NDA executed", "Permitted use defined", "Access expiry date defined", "Copying and onward disclosure restricted", "IP ownership acknowledged"];

const auDate = (iso) => new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const exportCsv = async (name) => {
  try {
    const res = await http.get(`/export/${name}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${name}.csv`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Export failed."); }
};

export default function BriefingsPanel() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ organisation_type: "", engagement_type: "", budget_range: "", status: "" });
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    http.get("/briefing-requests").then(({ data }) => setRows(data)).catch(() => { setRows([]); toast.error("Could not load briefing requests."); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await http.patch(`/briefing-requests/${id}`, { status }).catch(() => toast.error("Update failed."));
    load();
  };
  const toggleCheck = async (row, item) => {
    const nda_checklist = { ...(row.nda_checklist || {}), [item]: !(row.nda_checklist || {})[item] };
    await http.patch(`/briefing-requests/${row.id}`, { nda_checklist }).catch(() => toast.error("Update failed."));
    load();
  };

  const sendCapability = async (id) => {
    try {
      await http.post(`/portal/admin/briefings/${id}/send-capability`);
      toast.success("Capability statement emailed (7-day link).");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Send failed.");
    }
  };

  const opts = (key) => [...new Set((rows || []).map((r) => r[key]).filter(Boolean))];
  const filtered = (rows || []).filter((r) => Object.entries(filters).every(([k, v]) => !v || r[k] === v));

  return (
    <div data-testid="briefings-panel">
      <BriefingAnalytics />
      <div className="flex flex-wrap items-center gap-3 mb-8">
        {[["organisation_type", "Org type"], ["engagement_type", "Engagement"], ["budget_range", "Budget"], ["status", "Status"]].map(([k, label]) => (
          <select key={k} data-testid={`filter-${k}`} className="field-dark px-3 py-2 text-xs" value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}>
            <option value="">{label}: all</option>
            {(k === "status" ? STATUSES : opts(k)).map((o) => <option key={o}>{o}</option>)}
          </select>
        ))}
        <button onClick={() => window.open(`${API}/portal/admin/capability-statement.pdf`, "_blank")} data-testid="capability-pdf-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 border border-line text-stone2 hover:border-ember hover:text-ember transition-colors duration-300 flex items-center gap-2 ml-auto">
          <FileText size={13} /> Capability PDF
        </button>
        <button onClick={() => exportCsv("briefing-requests")} data-testid="export-briefings-btn" className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 flex items-center gap-2">
          <Download size={13} /> CSV
        </button>
      </div>

      {rows === null ? (
        <div className="border border-line bg-surface1 p-16 text-center text-stone2 font-mono text-xs uppercase tracking-[0.3em]" data-testid="briefings-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-line bg-surface1 p-16 text-center text-stone2">No briefing requests match.</div>
      ) : (
        <div className="space-y-px bg-line/40 border border-line/40">
          {filtered.map((r, i) => (
            <article key={r.id} data-testid={`briefing-row-${i}`} className="bg-ink p-5 md:p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex flex-wrap items-baseline gap-3">
                  <h3 className="font-serif text-lg text-bone">{r.full_name}</h3>
                  <span className="font-mono text-xs text-stone2">{r.position} · {r.organisation}</span>
                  <a href={`mailto:${r.email}`} className="font-mono text-xs text-ember">{r.email}</a>
                </div>
                <select data-testid={`status-select-${i}`} className="field-dark px-3 py-1.5 text-xs" value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}>
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 font-mono text-[10px] uppercase tracking-[0.12em]">
                {[r.organisation_type, r.engagement_type, r.budget_range, r.state_territory].map((t) => (
                  <span key={t} className="px-2.5 py-1 border border-line text-stone2">{t}</span>
                ))}
                <span className="px-2.5 py-1 border border-ember/40 text-ochre">Created {auDate(r.created_at)}</span>
                <span className="px-2.5 py-1 border border-line text-stone2">Updated {auDate(r.updated_at)}</span>
              </div>
              <button onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`briefing-expand-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] text-ochre hover:text-ember transition-colors duration-300 mt-4">
                {open === r.id ? "Hide details" : "View details"}
              </button>
              {open === r.id && (
                <div className="mt-4 space-y-3 text-sm text-stone2 border-t border-line/60 pt-4">
                  {[["Phone", r.phone], ["Project / system", r.project_system], ["Governance problem", r.governance_problem], ["Communities / knowledge affected", r.communities_affected], ["Desired outcome", r.desired_outcome], ["Decision-makers", r.decision_makers], ["Commencement", r.commencement_date]].map(([l, v]) => (
                    <p key={l}><span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ochre">{l}: </span>{v}</p>
                  ))}
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <button onClick={() => sendCapability(r.id)} data-testid={`send-capability-${i}`} className="font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 bg-ember text-ink hover:bg-emberDeep transition-colors duration-300 flex items-center gap-1.5">
                      <Send size={12} /> {r.capability_sent_at ? "Resend capability statement" : "Send capability statement"}
                    </button>
                    {r.capability_sent_at && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ochre" data-testid={`capability-sent-${i}`}>Sent {auDate(r.capability_sent_at)}</span>
                    )}
                  </div>
                  {r.status === "NDA required" && (
                    <div data-testid={`nda-stage-${i}`} className="border border-ember/40 bg-surface1 p-5 mt-4">
                      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ember mb-4">Prepare NDA stage — internal checklist (no automatic disclosure)</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {CHECKLIST.map((item) => (
                          <label key={item} className="flex items-start gap-2 text-xs text-stone2 cursor-pointer">
                            <input type="checkbox" checked={!!(r.nda_checklist || {})[item]} onChange={() => toggleCheck(r, item)} className="mt-0.5 accent-[#B44A2C]" />
                            {item}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
